import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sun, ShieldCheck, Thermometer, Wand2 } from "lucide-react";
import QuoteDialog from "@/components/QuoteDialog";
import { Slider } from "@/components/ui/slider";
import { VEHICLES } from "@/data/catalog";
import { composeTintImage } from "@/lib/buildImage";

const VLT_LEVELS = [
  { vlt: 5, label: "Limo", alpha: 0.97, light: 0.0, heat: 92, glare: 97 },
  { vlt: 20, label: "Dark", alpha: 0.82, light: 0.12, heat: 84, glare: 88 },
  { vlt: 35, label: "Medium", alpha: 0.58, light: 0.28, heat: 74, glare: 76 },
  { vlt: 50, label: "Light", alpha: 0.34, light: 0.48, heat: 64, glare: 60 },
  { vlt: 70, label: "Clear Ceramic", alpha: 0.12, light: 0.72, heat: 52, glare: 38 },
];

const money = (n) => `$${Math.round(n).toLocaleString()}`;

const zoneMask = (src) => ({
  WebkitMaskImage: `url(${src})`,
  maskImage: `url(${src})`,
  WebkitMaskSize: "100% 100%",
  maskSize: "100% 100%",
  maskRepeat: "no-repeat",
});

const TintLab = () => {
  const [vehicleId, setVehicleId] = useState("gwagon");
  const [levelIdx, setLevelIdx] = useState(1);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [buildImage, setBuildImage] = useState(null);

  const vehicle = VEHICLES.find((v) => v.id === vehicleId);
  const level = VLT_LEVELS[levelIdx];
  const price = vehicle.tintPrice || 399;

  useEffect(() => {
    VEHICLES.forEach((v) => {
      [v.img, v.glassMask].filter(Boolean).forEach((src) => {
        const i = new Image();
        i.src = src;
      });
    });
  }, []);

  const openQuote = () => {
    setQuoteOpen(true);
    setBuildImage(null);
    composeTintImage({ vehicle, level })
      .then((c) => setBuildImage(c.toDataURL("image/jpeg", 0.85)))
      .catch(() => {});
  };

  const preset = useMemo(
    () => ({
      source: "tint_lab",
      service: "Ceramic Window Tint",
      vehicle_type: vehicle.name,
      buildImage,
      notePrefix: `Tint Lab build — VLT ${level.vlt}% (${level.label}) on ${vehicle.name}, est. ${money(price)}.`,
      summary: `VEHICLE — ${vehicle.name}\nFILM — Nano-Ceramic IR\nDARKNESS — VLT ${level.vlt}% (${level.label})\nESTIMATE — ${money(price)}`,
    }),
    [vehicle, level, price, buildImage]
  );

  return (
    <main data-testid="tint-lab-view" className="pt-24 pb-16 sm:pb-20 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <p className="font-mono text-xs tracking-[0.25em] text-velocity uppercase font-bold mb-3">The Tint Lab</p>
          <h1 className="font-display font-black uppercase tracking-tight text-3xl sm:text-6xl leading-none">
            Dial In Your <span className="text-velocity">Darkness.</span>
          </h1>
          <p className="mt-4 max-w-xl text-slate-400 leading-relaxed text-sm sm:text-base">
            Pick your ride, drag the VLT slider, and watch the cabin glass respond — nano-ceramic film, computer-cut
            to your exact windows.
          </p>
        </motion.div>

        <div className="mt-8 sm:mt-10 grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* CANVAS */}
          <div className="lg:col-span-3">
            <div className="lg:sticky lg:top-24">
              <div className="relative rounded-2xl border border-edge bg-gunmetal blueprint-grid overflow-hidden">
                <div className="flex items-center justify-between px-4 sm:px-5 py-3 border-b border-edge/70">
                  <span className="font-mono text-[10px] tracking-[0.25em] text-steel uppercase">
                    Live Preview · Side Glass
                  </span>
                  <span
                    data-testid="tint-vlt-label"
                    className="font-mono text-[10px] tracking-[0.25em] text-velocity uppercase"
                  >
                    VLT {level.vlt}% · {level.label}
                  </span>
                </div>
                <div
                  data-testid="tint-stage"
                  className="relative w-full overflow-hidden"
                  style={{
                    aspectRatio: "16 / 9",
                    background: "radial-gradient(ellipse 80% 75% at 50% 38%, #1B212B 0%, #0A0B0E 78%)",
                  }}
                >
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={vehicleId}
                      src={vehicle.img}
                      alt={`${vehicle.name} window tint preview`}
                      initial={{ opacity: 0, x: 40 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -40 }}
                      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                      className="absolute inset-0 h-full w-full"
                      style={{ objectFit: "fill" }}
                      draggable="false"
                    />
                  </AnimatePresence>
                  {vehicle.glassMask && (
                    <>
                      <div
                        data-testid="tint-glass-light"
                        className="absolute inset-0 transition-opacity duration-500"
                        style={{
                          ...zoneMask(vehicle.glassMask),
                          background:
                            "linear-gradient(160deg, rgba(216,226,236,0.95) 0%, rgba(168,182,198,0.8) 45%, rgba(120,136,156,0.6) 100%)",
                          opacity: level.light,
                        }}
                      />
                      <div
                        data-testid="tint-glass-overlay"
                        className="absolute inset-0 transition-opacity duration-500"
                        style={{ ...zoneMask(vehicle.glassMask), background: "#04060B", opacity: level.alpha }}
                      />
                    </>
                  )}
                </div>
              </div>

              <div className="mt-4 grid grid-cols-3 gap-3">
                {[
                  { icon: Thermometer, v: `${level.heat}%`, l: "Solar Heat Rejected", testid: "tint-stat-heat" },
                  { icon: Sun, v: `${level.glare}%`, l: "Glare Eliminated", testid: "tint-stat-glare" },
                  { icon: ShieldCheck, v: "99%", l: "UV Blocked", testid: "tint-stat-uv" },
                ].map((s) => (
                  <div key={s.l} data-testid={s.testid} className="rounded-xl border border-edge bg-panel p-3 sm:p-4 text-center">
                    <s.icon className="h-4 w-4 text-velocity mx-auto mb-1.5" />
                    <p className="font-display font-black text-xl sm:text-2xl text-white">{s.v}</p>
                    <p className="font-mono text-[9px] sm:text-[10px] tracking-[0.15em] text-steel uppercase mt-1">{s.l}</p>
                  </div>
                ))}
              </div>

              <div className="mt-4 rounded-2xl border border-velocity/40 bg-panel p-4 sm:p-5 flex flex-wrap items-center justify-between gap-4 shadow-[0_0_30px_rgba(255,42,59,0.12)]">
                <div>
                  <p className="font-mono text-[10px] tracking-[0.25em] text-steel uppercase">Full-Vehicle Ceramic Tint</p>
                  <p data-testid="tint-price-estimate" className="font-display font-black text-2xl sm:text-3xl text-white">
                    {money(price)}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-1">Ballpark — confirmed after glass measurement.</p>
                </div>
                <button
                  data-testid="tint-submit-quote-btn"
                  onClick={openQuote}
                  className="inline-flex items-center gap-2 bg-velocity hover:bg-velocitydark text-white text-xs sm:text-sm font-semibold px-4 py-2.5 sm:px-6 sm:py-3 rounded-full transition-all duration-200 shadow-[0_0_24px_rgba(255,42,59,0.4)] hover:-translate-y-0.5"
                >
                  <Wand2 className="h-4 w-4" /> Quote Me at VLT {level.vlt}%
                </button>
              </div>
            </div>
          </div>

          {/* CONTROLS */}
          <div className="lg:col-span-2 space-y-6 sm:space-y-8">
            <section>
              <p className="font-mono text-xs tracking-[0.25em] text-velocity uppercase font-bold mb-3">
                <span className="text-steel mr-2">01</span>Vehicle
              </p>
              <div className="grid grid-cols-2 gap-2">
                {VEHICLES.map((v) => (
                  <button
                    key={v.id}
                    data-testid={`tint-vehicle-select-${v.id}`}
                    onClick={() => setVehicleId(v.id)}
                    className={`text-left rounded-xl border p-3 sm:p-4 transition-all duration-200 ${
                      vehicleId === v.id
                        ? "border-velocity bg-velocity/10 shadow-[0_0_18px_rgba(255,42,59,0.15)]"
                        : "border-edge bg-panel hover:border-steel"
                    }`}
                  >
                    <p className="font-display font-bold text-sm tracking-tight leading-tight">{v.name}</p>
                    <p className="font-mono text-[10px] text-velocity mt-2">FULL TINT {money(v.tintPrice || 399)}</p>
                  </button>
                ))}
              </div>
            </section>

            <section>
              <p className="font-mono text-xs tracking-[0.25em] text-velocity uppercase font-bold mb-3">
                <span className="text-steel mr-2">02</span>Darkness — VLT {level.vlt}%
              </p>
              <div className="grid grid-cols-5 gap-1.5 mb-4">
                {VLT_LEVELS.map((l, i) => (
                  <button
                    key={l.vlt}
                    data-testid={`tint-vlt-select-${l.vlt}`}
                    onClick={() => setLevelIdx(i)}
                    className={`rounded-lg border py-2.5 text-center transition-all duration-200 ${
                      levelIdx === i ? "border-velocity bg-velocity/10" : "border-edge bg-panel hover:border-steel"
                    }`}
                  >
                    <span className="block font-display font-black text-sm">{l.vlt}%</span>
                    <span className="block font-mono text-[8px] sm:text-[9px] tracking-wider text-steel uppercase mt-0.5">
                      {l.label}
                    </span>
                  </button>
                ))}
              </div>
              <Slider
                data-testid="tint-vlt-slider"
                value={[levelIdx]}
                min={0}
                max={4}
                step={1}
                onValueChange={([i]) => setLevelIdx(i)}
                className="py-2"
              />
              <div className="flex justify-between font-mono text-[10px] text-steel mt-1">
                <span>Darker</span>
                <span>Lighter</span>
              </div>
            </section>

            <section className="rounded-xl border border-edge bg-panel p-4 sm:p-5">
              <p className="font-mono text-[10px] tracking-[0.25em] text-velocity uppercase font-bold mb-2">
                Keep It Legal
              </p>
              <p className="text-xs text-slate-400 leading-relaxed">
                Arkansas sets minimum light levels for front side glass — we'll measure your windows and dial in a
                setup that looks mean and stays legal at your appointment.
              </p>
            </section>

            <section className="rounded-xl border border-edge bg-panel p-4 sm:p-5">
              <p className="font-mono text-[10px] tracking-[0.25em] text-velocity uppercase font-bold mb-2">
                Every Install Includes
              </p>
              <ul className="text-xs text-slate-400 space-y-1.5">
                <li>Nano-ceramic IR film — no signal interference</li>
                <li>Computer-cut patterns, hand-finished edges</li>
                <li>Lifetime fade &amp; bubble warranty</li>
              </ul>
            </section>
          </div>
        </div>
      </div>

      <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} preset={preset} />
      <section data-testid="tint-lab-info" className="max-w-3xl mx-auto px-4 sm:px-8 mt-12 sm:mt-16 border-t border-edge pt-8 sm:pt-10">
        <h2 className="font-display font-bold text-xl sm:text-2xl tracking-tight mb-4">Ceramic Window Tint Preview for Northwest Arkansas Drivers</h2>
        <div className="space-y-4 text-sm text-slate-400 leading-relaxed">
          <p>The Tint Lab lets you preview nano-ceramic window film at five darkness levels — 5% limo, 20% dark, 35% medium, 50% light and 70% clear ceramic — on the actual body style you drive. Slide between levels and the cabin glass responds instantly while the heat rejection and glare numbers update with it. Ceramic IR film blocks up to 99% of UV radiation and the majority of infrared heat, which keeps interiors cooler through Arkansas summers, protects leather and dashboards from fading, and cuts headlight glare on night drives down I-49.</p>
          <p>Every tint install at our Springdale studio is computer-cut to your exact windows and hand-finished for a factory-clean edge — no blades on your glass, no gaps, no purple fade. Nano-ceramic film is signal-safe, so GPS, phone and toll tags keep working. Arkansas law sets minimum light transmission for front side glass, and we'll measure your windows and recommend a setup that looks aggressive and stays street-legal. Full-vehicle ceramic tint packages start under $500 for most cars, trucks and SUVs, backed by a lifetime fade and bubble warranty.</p>
        </div>
      </section>

    </main>
  );
};

export default TintLab;
